import { useState } from "react";
import type {
  CancelReasonCode,
  DeclineReasonCode,
  OrderStatus,
  SellerOrderDetail,
} from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useUpdateOrderStatusMutation } from "./useUpdateOrderStatusMutation";

type Translator = ReturnType<typeof useTranslations>;

export type OrderTransitionPayload = {
  readonly status: OrderStatus;
  readonly statusEtaDays: number | null;
  readonly declineReasonCode?: DeclineReasonCode;
  readonly declineReasonNote?: string;
  readonly cancelReasonCode?: CancelReasonCode;
  readonly cancelReasonNote?: string;
};

export type UseOrderTransitionArgs = {
  readonly storeSlug: string;
  readonly orderId: string;
  readonly onUpdateStatus?: (
    payload: OrderTransitionPayload,
  ) => Promise<SellerOrderDetail>;
  readonly onSuccess?: (updatedOrder: SellerOrderDetail) => void;
};

export type UseOrderTransitionResult = {
  readonly submit: (payload: OrderTransitionPayload) => Promise<void>;
  readonly isSaving: boolean;
  readonly error: string | null;
  readonly success: string | null;
  readonly clearError: () => void;
  readonly clearSuccess: () => void;
};

function mapStatusUpdateError(
  message: string,
  nextStatus: OrderStatus,
  t: Translator,
): string {
  const normalized = message.toLowerCase();
  const isEtaError =
    normalized.includes("statusetadays") ||
    normalized.includes("eta") ||
    normalized.includes("status eta");
  if (isEtaError) {
    if (nextStatus === "shipped" || nextStatus === "preparing") {
      return t(keys.orders.etaRequiredForStatusUpdate);
    }
    return t(keys.orders.etaMustBeValidNumber);
  }
  return t(keys.errors.generic);
}

function impactMessage(status: OrderStatus, t: Translator): string {
  switch (status) {
    case "confirmed":
      return t(keys.seller.orderStatusConfirmed);
    case "preparing":
      return t(keys.seller.orderStatusPreparing);
    case "shipped":
      return t(keys.seller.orderStatusShipped);
    case "delivered":
      return t(keys.seller.orderStatusDelivered);
    default:
      return t(keys.orders.statusSavedMicro);
  }
}

/**
 * Encapsulates the seller-side order status transition: mutation call,
 * success/error state, and message mapping. Callers validate inputs
 * (ETA presence, decline reason presence) before invoking `submit`.
 *
 * Pass `onUpdateStatus` when the hosting component controls the mutation
 * (e.g. admin ops); otherwise the hook uses the seller mutation directly.
 */
export function useOrderTransition({
  storeSlug,
  orderId,
  onUpdateStatus,
  onSuccess,
}: UseOrderTransitionArgs): UseOrderTransitionResult {
  const t = useTranslations();
  const { mutateAsync: updateSellerOrderStatus } =
    useUpdateOrderStatusMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async (payload: OrderTransitionPayload): Promise<void> => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = onUpdateStatus
        ? await onUpdateStatus(payload)
        : await updateSellerOrderStatus({
            storeSlug,
            orderId,
            ...payload,
          });
      const message = impactMessage(payload.status, t);
      setSuccess(message);
      onSuccess?.(updated);
    } catch (caught) {
      if (caught instanceof ApiJsonClientError) {
        setError(mapStatusUpdateError(caught.message, payload.status, t));
      } else {
        setError(t(keys.errors.generic));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return {
    submit,
    isSaving,
    error,
    success,
    clearError: () => setError(null),
    clearSuccess: () => setSuccess(null),
  };
}
